/**
 * Kalshi API Service
 * Handles authentication and data fetching from Kalshi prediction markets
 * Documentation: https://docs.kalshi.com
 */

// Kalshi API Configuration
const KALSHI_API_KEY = import.meta.env.VITE_KALSHI_API_KEY;
const KALSHI_API_SECRET = import.meta.env.VITE_KALSHI_API_SECRET;
const KALSHI_ENVIRONMENT = import.meta.env.VITE_KALSHI_ENVIRONMENT || 'demo';
const KALSHI_REFERRAL_CODE = import.meta.env.VITE_KALSHI_REFERRAL_CODE;

// Use relative URL - works with both local dev server and Cloudflare Functions
// In development: Vite proxy will forward to Express server
// In production: Cloudflare Functions handle these routes
const PROXY_URL = import.meta.env.DEV 
  ? 'http://localhost:3001/api/kalshi'
  : '/api/kalshi';

// Note: With API key authentication, we don't need token caching
// The private key is used directly as the bearer token

/**
 * Authenticate with Kalshi API using API Key
 * @returns {Promise<string>} - Private key for authentication
 */
const authenticate = async () => {
  if (!KALSHI_API_KEY || !KALSHI_API_SECRET) {
    throw new Error('Kalshi API credentials not configured. Please set VITE_KALSHI_API_KEY (API Key ID) and VITE_KALSHI_API_SECRET (Private Key) in .env file');
  }

  // With API key authentication, we use the private key directly
  // No need to call login endpoint
  return KALSHI_API_SECRET;
};

/**
 * Make a request to our proxy server (which handles Kalshi API calls)
 * @param {string} endpoint - Proxy endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - API response data
 */
const proxyRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${PROXY_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Proxy request failed (${response.status}): ${errorData.error || response.statusText}`);
  }

  return response.json();
};

/**
 * Extract market ticker from Kalshi URL
 * @param {string} url - Kalshi market URL
 * @returns {string|null} - Market ticker or null
 */
export const extractTickerFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    // Kalshi URLs can have different structures:
    // https://kalshi.com/markets/TICKER
    // https://kalshi.com/events/EVENT/markets/TICKER
    // https://kalshi.com/markets/kxf1racepodium/f1-race-podium-finisher/kxf1racepodium-unisgp25
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    
    // Find 'markets' in the path and get the next part
    const marketsIndex = pathParts.findIndex(part => part === 'markets');
    if (marketsIndex !== -1 && pathParts[marketsIndex + 1]) {
      const ticker = pathParts[marketsIndex + 1];
      console.log('Extracted ticker from markets position:', ticker);
      return ticker;
    }

    // Alternative: ticker might be the last part of the path
    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart && lastPart.length > 3) {
      console.log('Extracted ticker from last position:', lastPart);
      return lastPart;
    }

    console.log('Could not extract ticker from URL:', url);
    return null;
  } catch (error) {
    console.error('Error extracting ticker from URL:', error);
    return null;
  }
};

/**
 * Get market details by ticker
 * @param {string} ticker - Market ticker symbol
 * @returns {Promise<Object>} - Market data
 */
export const getMarket = async (ticker) => {
  if (!ticker) {
    throw new Error('Market ticker is required');
  }

  try {
    const data = await proxyRequest(`/markets/${ticker}`);
    return data.market;
  } catch (error) {
    console.error(`Error fetching market ${ticker}:`, error);
    throw error;
  }
};

/**
 * Get order book (current prices) for a market
 * @param {string} ticker - Market ticker symbol
 * @returns {Promise<Object>} - Order book with current bid/ask prices
 */
export const getOrderBook = async (ticker) => {
  if (!ticker) {
    throw new Error('Market ticker is required');
  }

  try {
    const data = await proxyRequest(`/markets/${ticker}/orderbook`);
    return data.orderbook;
  } catch (error) {
    console.error(`Error fetching orderbook for ${ticker}:`, error);
    throw error;
  }
};

/**
 * Get current market price (best bid/ask midpoint)
 * @param {string} ticker - Market ticker symbol
 * @returns {Promise<Object>} - Current price information
 */
export const getCurrentPrice = async (ticker) => {
  if (!ticker) {
    throw new Error('Market ticker is required');
  }

  try {
    // Use the proxy server's combined endpoint
    const data = await proxyRequest(`/markets/${ticker}/price`);
    return data;
  } catch (error) {
    console.error(`Error getting current price for ${ticker}:`, error);
    throw error;
  }
};

/**
 * Get live prices for multiple markets
 * @param {Array<string>} tickers - Array of market ticker symbols
 * @returns {Promise<Object>} - Map of ticker to price data
 */
export const getLivePrices = async (tickers) => {
  if (!tickers || !Array.isArray(tickers) || tickers.length === 0) {
    return {};
  }

  // Filter out null/undefined tickers
  const validTickers = tickers.filter(Boolean);

  if (validTickers.length === 0) {
    return {};
  }

  try {
    // Use the proxy server's batch endpoint
    const response = await proxyRequest('/markets/prices', {
      method: 'POST',
      body: JSON.stringify({ tickers: validTickers }),
    });

    return response;
  } catch (error) {
    console.error('Error fetching live prices:', error);
    throw error;
  }
};

/**
 * Get live price data for an idea
 * @param {Object} idea - Idea object with marketUrl
 * @returns {Promise<Object|null>} - Live price data or null
 */
export const getLivePriceForIdea = async (idea) => {
  if (!idea || idea.platform !== 'Kalshi' || !idea.marketUrl) {
    return null;
  }

  // Only fetch for active ideas
  if (idea.status !== 'active') {
    return null;
  }

  try {
    const ticker = extractTickerFromUrl(idea.marketUrl);
    if (!ticker) {
      console.warn(`Could not extract ticker from URL: ${idea.marketUrl}`);
      return null;
    }

    const priceData = await getCurrentPrice(ticker);
    return priceData;
  } catch (error) {
    console.error(`Error fetching live price for idea ${idea.id}:`, error);
    return null;
  }
};

/**
 * Check if Kalshi API is configured
 * @returns {boolean} - True if API credentials are set
 */
export const isKalshiConfigured = () => {
  return !!(KALSHI_API_KEY && KALSHI_API_SECRET);
};

/**
 * Get Kalshi environment (production or demo)
 * @returns {string} - Current environment
 */
export const getEnvironment = () => {
  return KALSHI_ENVIRONMENT;
};

/**
 * Add referral code to Kalshi URL
 * @param {string} url - Original Kalshi market URL
 * @returns {string} - URL with referral parameter if configured
 */
export const addKalshiReferral = (url) => {
  // Return original URL if it's not a Kalshi URL or no referral code is configured
  if (!url || !url.includes('kalshi.com') || !KALSHI_REFERRAL_CODE) {
    return url;
  }
  
  try {
    const urlObj = new URL(url);
    // Add referral parameter to the URL (Kalshi uses 'referral' parameter)
    urlObj.searchParams.set('referral', KALSHI_REFERRAL_CODE);
    return urlObj.toString();
  } catch (error) {
    console.error('Error adding referral to Kalshi URL:', error);
    return url; // Return original URL if there's an error
  }
};

